import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaExternalLinkAlt, FaFileInvoiceDollar, FaExclamationTriangle, FaFileAlt, FaSpinner } from 'react-icons/fa';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent } from '../../services/seguranca';

interface PaymentItem {
  id: string;
  value: number;
  description: string;
  billingType: string;
  dueDate: string;
  status: string;
  invoiceUrl: string;
}

const statusMap: Record<string, { label: string; color: string; bg: string }> = {
  CONFIRMED: { label: 'Pago', color: '#16a34a', bg: '#f0fdf4' },
  RECEIVED: { label: 'Pago', color: '#16a34a', bg: '#f0fdf4' },
  PENDING: { label: 'Pendente', color: '#d97706', bg: '#fffbeb' },
  OVERDUE: { label: 'Atrasado', color: '#dc2626', bg: '#fef2f2' },
  REFUNDED: { label: 'Estornado', color: '#7c3aed', bg: '#f5f3ff' },
  REFUND_REQUESTED: { label: 'Estorno solicitado', color: '#7c3aed', bg: '#f5f3ff' },
  CHARGEBACK_REQUESTED: { label: 'Chargeback', color: '#dc2626', bg: '#fef2f2' },
  CHARGEBACK_DISPUTE: { label: 'Em disputa', color: '#dc2626', bg: '#fef2f2' },
  AWAITING_CHARGEBACK_REVERSAL: { label: 'Aguardando reversao', color: '#d97706', bg: '#fffbeb' },
  DUNNING_REQUESTED: { label: 'Negativado', color: '#dc2626', bg: '#fef2f2' },
  DUNNING_RECEIVED: { label: 'Pago', color: '#16a34a', bg: '#f0fdf4' },
  AWAITING_RISK_ANALYSIS: { label: 'Em analise', color: '#6b7280', bg: '#f9fafb' },
  RECEIVED_IN_CASH: { label: 'Pago', color: '#16a34a', bg: '#f0fdf4' },
};

const billingMap: Record<string, string> = {
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartao de Credito',
  PIX: 'PIX',
  UNDEFINED: '-',
};

const Container = styled.div`
  padding: 8px 24px 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button<{ active: boolean }>`
  padding: 6px 14px;
  border-radius: 8px;
  border: 1.5px solid ${p => p.active ? '#1e40af' : '#e2e8f0'};
  background: ${p => p.active ? '#eff6ff' : '#fff'};
  color: ${p => p.active ? '#1e40af' : '#64748b'};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: #93c5fd; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const Thead = styled.thead`
  background: #f8fafc;
`;

const Th = styled.th`
  padding: 10px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid #e2e8f0;

  &:first-child { border-radius: 8px 0 0 0; }
  &:last-child { border-radius: 0 8px 0 0; text-align: center; }
`;

const Tr = styled.tr`
  transition: background 0.1s;
  &:hover { background: #f8fafc; }
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.88rem;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
`;

const StatusBadge = styled.span<{ color: string; bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${p => p.color};
  background: ${p => p.bg};
`;

const InvoiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 6px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.12s;

  &:hover { background: #dbeafe; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #94a3b8;
  font-size: 0.92rem;
`;

const Loading = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #94a3b8;
  font-size: 0.92rem;
`;

// ======== ALERTA INADIMPLENCIA ========
const AlertBanner = styled.div`
  background: #fef2f2;
  border: 1.5px solid #fca5a5;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const AlertIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 4px;
`;

const AlertDesc = styled.p`
  font-size: 0.85rem;
  color: #b91c1c;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const AlertValue = styled.span`
  font-weight: 800;
  font-size: 1.1rem;
`;

const PayBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s;
  border: none;
  cursor: pointer;

  &:hover { background: #b91c1c; color: #fff; }
`;

const NfseBtn = styled.button<{ loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  opacity: ${p => p.loading ? 0.6 : 1};

  &:hover { background: #f1f5f9; border-color: #cbd5e1; }
`;

type FilterStatus = 'todos' | 'pagos' | 'pendentes' | 'atrasados';

const FaturasGrid: React.FC = () => {
  const token = useAppSelector(state => state.token);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('todos');
  const [nfseLoading, setNfseLoading] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const res = await api.get(`/api/payment/subscription/history/${obterIdTenent(token)}`);
      setPayments(res.data || []);
    } catch {
      toast.error('Erro ao carregar historico de pagamentos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const paidStatuses = ['CONFIRMED', 'RECEIVED', 'RECEIVED_IN_CASH', 'DUNNING_RECEIVED'];
  const pendingStatuses = ['PENDING', 'AWAITING_RISK_ANALYSIS'];
  const overdueStatuses = ['OVERDUE', 'DUNNING_REQUESTED'];

  const filtered = payments.filter(p => {
    if (filter === 'pagos') return paidStatuses.includes(p.status);
    if (filter === 'pendentes') return pendingStatuses.includes(p.status);
    if (filter === 'atrasados') return overdueStatuses.includes(p.status);
    return true;
  });

  const faturasAtrasadas = payments.filter(p => overdueStatuses.includes(p.status));
  const totalAtrasado = faturasAtrasadas.reduce((s, p) => s + p.value, 0);
  const faturaParaPagar = [...faturasAtrasadas].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

  const handleNfse = async (paymentId: string) => {
    setNfseLoading(paymentId);
    try {
      const res = await api.get(`/api/payment/nfse/${paymentId}`);
      const nfse = res.data;
      if (nfse.pdfUrl) {
        window.open(nfse.pdfUrl, '_blank');
      } else {
        toast.info('NFSe ainda nao disponivel para este pagamento');
      }
    } catch {
      toast.info('NFSe nao encontrada para este pagamento');
    } finally {
      setNfseLoading(null);
    }
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR');
  };

  if (loading) return <Loading>Carregando faturas...</Loading>;

  return (
    <Container>
      <Header>
        <Title><FaFileInvoiceDollar color="#1e40af" /> Faturas e Pagamentos</Title>
      </Header>

      {faturasAtrasadas.length > 0 && (
        <AlertBanner>
          <AlertIcon><FaExclamationTriangle /></AlertIcon>
          <AlertContent>
            <AlertTitle>
              {faturasAtrasadas.length === 1
                ? 'Voce possui 1 fatura em atraso'
                : `Voce possui ${faturasAtrasadas.length} faturas em atraso`}
            </AlertTitle>
            <AlertDesc>
              Regularize seu pagamento para manter o acesso completo ao sistema.
              {faturasAtrasadas.length === 1
                ? <> Valor: <AlertValue>{formatCurrency(totalAtrasado)}</AlertValue> — vencida em {formatDate(faturaParaPagar?.dueDate)}.</>
                : <> Total em aberto: <AlertValue>{formatCurrency(totalAtrasado)}</AlertValue>.</>
              }
            </AlertDesc>
            {faturaParaPagar?.invoiceUrl && (
              <PayBtn href={faturaParaPagar.invoiceUrl} target="_blank" rel="noopener noreferrer">
                <FaExternalLinkAlt size={12} /> Pagar agora
              </PayBtn>
            )}
          </AlertContent>
        </AlertBanner>
      )}

      <FilterRow>
        {([
          ['todos', `Todos (${payments.length})`],
          ['pagos', 'Pagos'],
          ['pendentes', 'Pendentes'],
          ['atrasados', `Atrasados${faturasAtrasadas.length > 0 ? ` (${faturasAtrasadas.length})` : ''}`],
        ] as [FilterStatus, string][]).map(([key, label]) => (
          <FilterBtn key={key} active={filter === key} onClick={() => setFilter(key)}>
            {label}
          </FilterBtn>
        ))}
      </FilterRow>

      {filtered.length === 0 ? (
        <EmptyState>Nenhuma fatura encontrada</EmptyState>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 16 }}>
          <Table>
            <Thead>
              <tr>
                <Th>Vencimento</Th>
                <Th>Descricao</Th>
                <Th>Forma</Th>
                <Th>Valor</Th>
                <Th>Status</Th>
                <Th>Fatura</Th>
                <Th>NFSe</Th>
              </tr>
            </Thead>
            <tbody>
              {filtered.map(p => {
                const st = statusMap[p.status] || { label: p.status, color: '#6b7280', bg: '#f9fafb' };
                const isOverdue = overdueStatuses.includes(p.status);
                return (
                  <Tr key={p.id} style={isOverdue ? { background: '#fef2f2' } : undefined}>
                    <Td style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{formatDate(p.dueDate)}</Td>
                    <Td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.description || '-'}
                    </Td>
                    <Td>{billingMap[p.billingType] || p.billingType}</Td>
                    <Td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatCurrency(p.value)}</Td>
                    <Td>
                      <StatusBadge color={st.color} bg={st.bg}>{st.label}</StatusBadge>
                    </Td>
                    <Td style={{ textAlign: 'center' }}>
                      {p.invoiceUrl ? (
                        <InvoiceLink href={p.invoiceUrl} target="_blank" rel="noopener noreferrer">
                          <FaExternalLinkAlt size={10} /> {isOverdue ? 'Pagar' : 'Ver'}
                        </InvoiceLink>
                      ) : '-'}
                    </Td>
                    <Td style={{ textAlign: 'center' }}>
                      {paidStatuses.includes(p.status) ? (
                        <NfseBtn
                          loading={nfseLoading === p.id}
                          disabled={nfseLoading === p.id}
                          onClick={() => handleNfse(p.id)}
                        >
                          {nfseLoading === p.id ? <FaSpinner size={10} className="spin" /> : <FaFileAlt size={10} />}
                          NFSe
                        </NfseBtn>
                      ) : '-'}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
};

export default FaturasGrid;
